import React, { useMemo } from "react";
import { Box, FormHelperText, Grid, TextField as MuiTextField } from "@mzc-pdc/ui";
import { IconErrorCircle, IconWarningTriangle } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { useTranslation } from "react-i18next";

const TextField = React.forwardRef(
    (
        {
            type,
            className,
            id,
            size,
            placeholder,
            error,
            helperText,
            multiline,
            length,
            maxLength = 255,
            sx,
            inputProps,
            value,
            label,
            defaultValue,
            disabled,
            onChange,
            onKeyDown,
            maxRows,
            inputStyle,
            isEdit,
            variant,
            height,
            ...rest
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const resolvedMaxRows = maxRows ?? (multiline ? 7 : undefined);

        const errorIcon = useMemo(() => {
            if (error === "warning") return <IconWarningTriangle color={"warning"} size={16} />;
            else if (error === "error") return <IconErrorCircle color={`error`} size={16} />;
            return undefined;
        }, [error]);

        const getEndAdornment = useMemo(() => {
            if (multiline) return undefined;
            return errorIcon;
        }, [error]);

        return (
            <Box
                sx={{
                    ...sx,
                    position: `relative`,
                }}
            >
                <MuiTextField
                    className={className}
                    variant={variant}
                    inputRef={ref}
                    type={type}
                    id={id}
                    label={label ? label : null}
                    size={size}
                    fullWidth
                    placeholder={placeholder}
                    error={error === "error"}
                    helperText={
                        error &&
                        helperText && (
                            <Grid container gap={0.75} alignItems={"center"}>
                                {/*{getEndAdornment}*/}
                                {helperText}
                            </Grid>
                        )
                    }
                    multiline={multiline}
                    maxRows={resolvedMaxRows}
                    defaultValue={defaultValue}
                    value={value}
                    disabled={disabled}
                    InputProps={{
                        ...inputProps,
                        ...{
                            endAdornment: getEndAdornment,
                        },
                    }}
                    InputLabelProps={{ shrink: true }}
                    onChange={onChange}
                    onKeyDown={onKeyDown}
                    {...rest}
                    sx={{
                        ...(height && {
                            "& .MuiInputBase-root": {
                                height,
                            },
                            "& .MuiOutlinedInput-input": {
                                padding: "0 14px",
                                height,
                                lineHeight: height,
                            },
                        }),
                        ...(isEdit && {
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: `#fff`,
                                borderRadius: "2px",
                                "> fieldset": { borderColor: theme.palette.primary[400] },
                            },
                        }),
                        ...(disabled && {
                            "& .MuiOutlinedInput-root .Mui-disabled": {
                                backgroundColor: `#fff`,
                            },
                        }),
                        ...(multiline && length >= 0 && {
                            "& .MuiOutlinedInput-input": {
                                marginBottom: "20px !important",
                            },
                        }),
                    }}
                />
                {multiline && length >= 0 ? (
                    <FormHelperText sx={{ mr: "14px" }}>
                        <Grid
                            component="span"
                            container
                            gap={0.5}
                            alignItems="center"
                            color={error ? theme.palette.error.main : null}
                        >
                            {error && errorIcon}
                            {length} / {maxLength}
                        </Grid>
                    </FormHelperText>
                ) : null}
            </Box>
        );
    },
);

export default TextField;
